import { Button, Stack } from '@mui/joy';
import { CellValueChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { useAuthUser } from 'auth/hooks';
import { AgGridTable, SlidingPanelContent } from 'core/components';
import { NotificationPosition, TableLayoutType } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import {
  AgGridClientTableProvider,
  SelectionCountProvider,
  useAgGridClientTableContext,
} from 'core/states';
import { AgGridDataRow, ISelectionActionProps } from 'core/types';
import { defaultShouldRowBeUpdated } from 'core/utils';
import { useGetPartBomItems, usePartBomItemActions } from 'partBoms/hooks';
import { IPartBomDetails, IPartBomItem, IUpsertPartBomItemPayload } from 'partBoms/types';
import { PartType } from 'parts/enums';
import { Color, Direction, Variant } from 'theme/enums';

import { getColumnDefs } from './columns';
import { DetailPartBomItem } from './DetailPartBomItem';
import { NoRowsOverlay } from './NoRowsOverlay';
import { RemoveItemsButton } from './RemoveItems';
import { AddItemButton } from './UpsertItem';
import { aggFuncs } from './utils';

interface IProps {
  partBom: IPartBomDetails;
  onClose?: () => void;
  onSaveItemList?: (items: IPartBomItem[]) => void;
  inline?: boolean;
  isPartBomEditable?: boolean;
}

const ItemListTable = ({
  partBom,
  onClose,
  onSaveItemList,
  inline = false,
  isPartBomEditable = true,
}: IProps) => {
  const { t } = useTranslation();

  const { rows } = useAgGridClientTableContext();

  const buttons = useMemo(() => {
    if (!onSaveItemList) {
      return null;
    }

    return (
      <Stack direction={Direction.ROW} gap={2}>
        <Button
          variant={Variant.SOLID}
          disabled={!rows.length}
          onClick={() => onSaveItemList(rows as IPartBomItem[])}
        >
          {t('buttonLabelCompleteAndAddToMyQuote')}
        </Button>
        <Button variant={Variant.SOFT} color={Color.NEUTRAL} onClick={() => onClose()}>
          {t('buttonLabelDiscardChanges')}
        </Button>
      </Stack>
    );
  }, [onSaveItemList, t, onClose, rows]);

  const actionButtons = useCallback(() => {
    if (!isPartBomEditable) {
      return [];
    }

    return [<AddItemButton key="addPartBomItemButton" partBom={partBom} />];
  }, [partBom, isPartBomEditable]);

  const selectionActionButtons = useCallback(
    ({ deselectAllRows, selectedRows }: ISelectionActionProps) => {
      if (!isPartBomEditable) {
        return [];
      }

      return [
        <RemoveItemsButton
          key="removeItemsButton"
          items={selectedRows as IPartBomItem[]}
          onRemoveSuccess={deselectAllRows}
        />,
      ];
    },
    [isPartBomEditable]
  );

  const table = useMemo(() => {
    return (
      <AgGridTable actionButtons={actionButtons} selectionActionButtons={selectionActionButtons} />
    );
  }, [actionButtons, selectionActionButtons]);

  if (inline) {
    return table;
  }

  return (
    <SlidingPanelContent title={t('labelPartBomItemList')} fullWidth={false} buttons={buttons}>
      {table}
    </SlidingPanelContent>
  );
};

export const ItemListWithoutSelectionCount = ({
  partBom,
  onClose,
  onSaveItemList,
  inline = false,
  isPartBomEditable = true,
}: IProps) => {
  const queryClient = useQueryClient();

  const { user } = useAuthUser();

  const { upsertPartBomItem } = usePartBomItemActions(NotificationPosition.DRAWER);

  const isAlternateVersionEnabled = useMemo(
    () => AlternateVersionHelper.isAlternateVersionEnabled({ user }),
    [user]
  );

  const defaultColDef = useMemo(() => ({ autoHeight: true }), []);

  const columnDefs = useMemo(
    () => getColumnDefs(partBom, isAlternateVersionEnabled),
    [partBom, isAlternateVersionEnabled]
  );

  const {
    records,
    isLoading: isDataLoading,
    loadingPercentage,
    currentPage,
  } = useGetPartBomItems(
    {
      filters: {
        parentPartId: partBom?.id,
      },
    },
    { fetchAllPages: true }
  );

  const onCellValueChanged = useCallback(
    async (
      _: AgGridReact,
      event: CellValueChangedEvent
    ): Promise<{ updatedRow: AgGridDataRow }> => {
      const { success, error, partBomItem } = await upsertPartBomItem({
        partBomItem: {
          id: event.data.id,
          [event.colDef.field]: event.newValue,
        },
      } as IUpsertPartBomItemPayload);

      if (!success) {
        throw new Error(error);
      }

      queryClient.invalidateQueries(['partBomItems']);

      return { updatedRow: partBomItem };
    },
    [queryClient, upsertPartBomItem]
  );

  const isRowMaster = useCallback(
    (dataItem: IPartBomItem) => dataItem.partArr?.type === PartType.BOM,
    []
  );

  const shouldRowBeUpdated = useCallback((newRow: AgGridDataRow, existingRow: AgGridDataRow) => {
    return defaultShouldRowBeUpdated(newRow, existingRow) || newRow?.cost !== existingRow?.cost;
  }, []);

  return (
    <AgGridClientTableProvider
      tableId={`partBomItemListTable-${partBom?.id}`}
      entityType="Line"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={records}
      isDataLoading={isDataLoading}
      loadingPercentage={loadingPercentage}
      dataCurrentPage={currentPage}
      aggFuncs={aggFuncs}
      onCellValueChanged={onCellValueChanged}
      layoutType={TableLayoutType.PART_BOM_ITEMS}
      noRowsOverlayComponent={NoRowsOverlay}
      noRowsOverlayComponentParams={{
        inline,
      }}
      headerCheckbox
      hasLayoutActions={!inline}
      masterDetail
      detailRowAutoHeight
      detailCellRenderer={DetailPartBomItem}
      isRowMaster={isRowMaster}
      shouldRowBeUpdated={shouldRowBeUpdated}
    >
      <ItemListTable
        partBom={partBom}
        onClose={onClose}
        onSaveItemList={onSaveItemList}
        inline={inline}
        isPartBomEditable={isPartBomEditable}
      />
    </AgGridClientTableProvider>
  );
};

export const ItemList = ({
  partBom,
  onClose,
  onSaveItemList,
  inline = false,
  isPartBomEditable = true,
}: IProps) => (
  <SelectionCountProvider>
    <ItemListWithoutSelectionCount
      partBom={partBom}
      onClose={onClose}
      onSaveItemList={onSaveItemList}
      inline={inline}
      isPartBomEditable={isPartBomEditable}
    />
  </SelectionCountProvider>
);
